<template>
    <IntegrationsTabs tab-key="active">
        <div v-if="integrations.length !== 0">
            <div
                class="flex w-full flex-wrap gap-x-4 gap-y-8 py-10 md:grid md:grid-cols-2 lg:grid-cols-3"
            >
                <SlackIntegrationBox
                    v-for="integration in slackIntegrations"
                    :key="integration.id"
                    :integration="integration"
                    active
                    @openEdit="openEditSlackIntegrationModal(integration)"
                    @openRemove="
                        openRemoveIntegrationModal(integration.id, 'Slack')
                    "
                />

                <TeamsIntegrationBox
                    v-for="integration in teamsIntegrations"
                    :key="integration.id"
                    :integration="integration"
                    active
                    @openEdit="openEditTeamsIntegrationModal(integration)"
                    @openRemove="
                        openRemoveIntegrationModal(integration.id, 'Teams')
                    "
                />
            </div>

            <RemoveIntegrationModal
                v-if="
                    removeIntegrationModalOpened &&
                    removeIntegrationType !== undefined
                "
                :integration-type="removeIntegrationType"
                @close="closeRemoveIntegrationModal()"
                @remove="deleteIntegration()"
            ></RemoveIntegrationModal>
        </div>

        <div
            v-else
            class="mt-6 flex w-full flex-col-reverse items-center justify-center rounded-[4px] border border-gray-200 text-center sm:flex-row sm:text-left"
        >
            <div>
                <h2 class="mb-2 max-w-md text-2xl font-medium text-gray-600">
                    {{ t("vue.havent_added_integrations") }}
                </h2>

                <p class="mb-8 max-w-md text-sm font-medium text-gray-500">
                    {{ t("vue.add_integrations_message") }}
                </p>

                <div class="flex justify-center sm:justify-start">
                    <inertia-link :href="route('integrations.available.index')">
                        <AtomicButton color="yellow" size="lg">
                            {{ t("vue.see_integrations") }}
                        </AtomicButton>
                    </inertia-link>
                </div>
            </div>
            <div class="ml-4 mt-10 shrink-0">
                <img
                    class="h-[240px] w-[240px]"
                    src="https://d5aoblv5p04cg.cloudfront.net/application/images/illustrations-empty.png"
                    alt="No templates added yet"
                />
            </div>
        </div>
    </IntegrationsTabs>
</template>

<script lang="ts" setup>
import { create } from "@composables/useNotifications";
import { ref, onMounted, type PropType, computed } from "vue";
import type {
    ISlack,
    ITeams,
    IIntegration,
} from "@/types/Integrations/IIntegration";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import useIntegrations from "../composables/useIntegrations";
import { useTranslations } from "@/composables/useTranslations";

const props = defineProps({
    integrations: {
        // eslint-disable-next-line @typescript-eslint/ban-types
        type: Array as PropType<IIntegration<{}>[]>,
        required: true,
    },
    features: {
        type: Object,
        default: null,
    },
    type: {
        type: String,
        default: null,
    },
});

// Get access to translations
const { t } = useTranslations();

const {
    slackIntegrationModalOpened,
    teamsIntegrationModalOpened,
    selectedIntegration,
} = useIntegrations();

const inertiaPostOptions = ref({
    preserveScroll: true,
    onSuccess: () => {
        create({
            title: t("vue.integration_added"),
            type: "success",
        });
    },
    onError: () => {
        create({
            title: t("vue.integration_not_added"),
            text: t("vue.error_adding_integration"),
            type: "error",
        });
    },
});

//Integrations
const removeIntegrationModalOpened = ref(false);
const removeIntegrationType = ref<string>();
const removeIntegrationId = ref<number>();

function openRemoveIntegrationModal(
    integrationId: number,
    integrationType: string,
) {
    removeIntegrationModalOpened.value = true;
    removeIntegrationId.value = integrationId;
    removeIntegrationType.value = integrationType;
}

function closeRemoveIntegrationModal() {
    removeIntegrationModalOpened.value = false;
    removeIntegrationId.value = undefined;
    removeIntegrationType.value = undefined;
}

function deleteIntegration() {
    router.delete(
        //@ts-ignore
        route("integrations.delete", {
            integration: removeIntegrationId.value,
        }),
        {
            preserveScroll: true,
            onSuccess: () => {
                create({
                    title: t("vue.integration_removed"),
                    text: t("vue.integration_removed_successfully"),
                    type: "success",
                });
            },
            onError: () => {
                create({
                    title: t("vue.integration_not_removed"),
                    text: t("vue.error_removing_integration"),
                    type: "error",
                });
            },
        },
    );
    // todo handle errors
    closeRemoveIntegrationModal();
}

//Slack
const slackIntegrations = computed((): IIntegration<ISlack>[] => {
    //@ts-ignore
    return props.integrations.filter((integration) => {
        if (integration.type === "SLACK") {
            return integration as IIntegration<ISlack>;
        }
    });
});

const openEditSlackIntegrationModal = (integration: IIntegration<ISlack>) => {
    slackIntegrationModalOpened.value = true;
    selectedIntegration.value = integration;
};

const createSlackIntegration = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const type = urlParams.get("type");

    if (code && type?.includes("slack")) {
        router.post(
            route("integrations.slack.store"),
            {
                code: code,
            },
            inertiaPostOptions.value,
        );
    }
};

//Teams
const teamsIntegrations = computed((): IIntegration<ITeams>[] => {
    //@ts-ignore
    return props.integrations.filter((integration) => {
        if (integration.type === "TEAMS") {
            return integration as IIntegration<ITeams>;
        }
    });
});

const openEditTeamsIntegrationModal = (integration: IIntegration<ITeams>) => {
    teamsIntegrationModalOpened.value = true;
    selectedIntegration.value = integration;
};

onMounted(() => {
    setTimeout(() => {
        createSlackIntegration();
    }, 500);
});
</script>
